// ===================================================================
// AssistantDashboard.tsx - Dashboard PRO para Secretarias (Español)
// ===================================================================

import React, { useState, useEffect, useRef } from 'react';
import LoadingSpinner from './LoadingSpinner';
import ComprobanteViewerModal from './ComprobanteViewerModal';
import AssistantServicesModal from './AssistantServicesModal';
import WeatherWidget from './WeatherWidget';
import { useNavigate } from 'react-router-dom';
import NewAppointmentModal from './NewAppointmentModal';
import ExportDateRangeModal from './ExportDateRangeModal';
import { createPortal } from 'react-dom';
import RemindersPanel from './RemindersPanel';
import NewRemindersGuideModal from './NewRemindersGuideModal';
import ReferralBadge from './ReferralBadge';
import AppointmentNoteEditor from './AppointmentNoteEditor';
import {
  Calendar,
  Users,
  Clock,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  Bell,
  User,
  Phone,
  FileText,
  AlertCircle,
  Search,
  Plus,
  Briefcase,
  CalendarCheck,
  CheckCircle,
  MessageCircle,
  Mail,
  FileSpreadsheet,
  LayoutGrid,
  MapPin,
  ChevronsUpDown,
  ChevronsDownUp,
  ChevronUp,
  ChevronDown,
  HelpCircle,
  Banknote,
  DollarSign,
  ListChecks
} from 'lucide-react';

// --- Interfaces ---
interface Professional {
  professional_id: number;
  nombre: string;
  apellido: string;
  especialidad: string;
  picture: string | null;
  subscription_status?: string;
  subscription_active?: boolean;
  subscription_label?: string;
  trial_ends_at?: string;
}

interface Invitation {
  invitation_id: number;
  professional_id: number;
  nombre: string;
  apellido: string;
  especialidad: string;
  picture: string | null;
  invited_at: string;
}

interface Appointment {
  id: number;
  professional_id: number;
  fecha: string;
  hora: string;
  estado: string;
  notas: string;
  client_nombre: string;
  client_apellido: string;
  client_telefono: string;
  service_name: string;
  service_duration: number;
  metodo_pago_elegido?: string;
  comprobante_pago_url?: string | null;
  pago_verificado?: boolean;
  reminder_sent?: boolean;
  reminder_sent_at?: string;
  whatsapp_confirmation?: {
    status: string;
    template_name: string;
    created_at: string;
    error_message: string | null;
  } | null;
  whatsapp_reminder?: {
    status: string;
    template_name: string;
    created_at: string;
    error_message: string | null;
  } | null;
  whatsapp_professional?: {
    status: string;
    created_at: string;
  } | null;
  email_confirmation?: {
    status: string;
    template_name: string;
    created_at: string;
    error_message: string | null;
  } | null;
  email_reminder?: {
    status: string;
    template_name: string;
    created_at: string;
    error_message: string | null;
  } | null;
  client_email?: string;
  location_id?: number;
  location_name?: string;
  location_city?: string;
  confirmed_by?: string;
  confirmed_at?: string;
  cancelled_by?: string;
  cancelled_at?: string;
  cancellation_reason?: string;
}

interface AgendaItem {
  professional: Professional;
  appointments: Appointment[];
}

// Fecha local en formato YYYY-MM-DD (NO usar toISOString, que devuelve UTC y de
// noche en Argentina —UTC-3— adelanta la fecha un día).
const getLocalDateStr = (d: Date = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const AssistantDashboard: React.FC = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // --- Estados ---
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [agenda, setAgenda] = useState<AgendaItem[]>([]);
  const [loadingAgenda, setLoadingAgenda] = useState(false); // carga al cambiar de fecha
  const [selectedDate, setSelectedDate] = useState(getLocalDateStr());
  const [processingInvitation, setProcessingInvitation] = useState<number | null>(null);
  
  // Modal de nuevo turno
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
  // Horarios de la profesional (oct-2026: la secretaria los puede ver y
  // cambiar; cada cambio queda anotado con su nombre). Con varias
  // profesionales y el filtro en "Todos", primero se elige de quién.
  const [elegirHorarios, setElegirHorarios] = useState(false);
  const irAHorarios = (id: number) => navigate(`/assistant/horarios/${id}`);
  // Modal de exportar agenda
  const [showExportModal, setShowExportModal] = useState(false);
  // Modal de precios de servicios
  const [showServicesModal, setShowServicesModal] = useState(false);
  // Nombre del usuario logueado (para el saludo)
  const [userName] = useState<string>(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').nombre || ''; } catch { return ''; }
  });
  // Modal de confirmación estético (reemplaza window.confirm)
  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    onConfirm: () => void;
  } | null>(null);
  // Adjuntar comprobante a un turno existente
  const comprobanteInputRef = useRef<HTMLInputElement | null>(null);
  const comprobanteTargetIdRef = useRef<number | null>(null);
  const [uploadingComprobanteId, setUploadingComprobanteId] = useState<number | null>(null);
  const [comprobanteViewerUrl, setComprobanteViewerUrl] = useState<string | null>(null);
  
  // Filtros y Búsqueda
  const [selectedProfFilter, setSelectedProfFilter] = useState<number | 'all'>('all');
  const [remindersPanelOpen, setRemindersPanelOpen] = useState(false);
  const [remindersPanelProfId, setRemindersPanelProfId] = useState<number | null>(null);
  const [showRemindersGuide, setShowRemindersGuide] = useState(false);

  /** Abre el panel de recordatorios. Si no hay profesional seleccionado y hay más de uno,
   *  pide elegir uno primero. Si solo hay uno, lo usa directo. */
  const openRemindersPanel = () => {
    if (selectedProfFilter !== 'all') {
      setRemindersPanelProfId(selectedProfFilter as number);
      setRemindersPanelOpen(true);
      return;
    }
    if (professionals.length === 1) {
      setRemindersPanelProfId(professionals[0].professional_id);
      setRemindersPanelOpen(true);
      return;
    }
    alert('Primero seleccioná un profesional en el filtro de arriba para ver sus recordatorios.');
  };
  const [selectedLocationFilter, setSelectedLocationFilter] = useState<number | 'all'>('all');

  // Turnos compactados: con agendas largas, ver todo el detalle cansa.
  // Guarda los ids colapsados; el botón del panel los aplica a todos.
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set());

  const toggleCollapsed = (id: number) => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Ids visibles hoy (respetando el filtro de sede) para el botón global
  const visibleAppointmentIds = agenda.flatMap(item =>
    item.appointments
      .filter(apt => selectedLocationFilter === 'all' || apt.location_id === selectedLocationFilter)
      .map(apt => apt.id)
  );
  const allCollapsed = visibleAppointmentIds.length > 0 &&
    visibleAppointmentIds.every(id => collapsedIds.has(id));

  const toggleCollapseAll = () => {
    setCollapsedIds(allCollapsed ? new Set() : new Set(visibleAppointmentIds));
  };
  const [searchTerm, setSearchTerm] = useState('');

  // Alerta de suscripción
  const [subscriptionWarning, setSubscriptionWarning] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // La guía de recordatorios ya NO se abre sola (a usuarios con localStorage
  // no persistente les aparecía en cada ingreso). Queda accesible desde el
  // botón "ver guía" del panel.

  useEffect(() => {
    if (professionals.length > 0) {
      loadUnifiedAgenda();
    }
  }, [selectedDate, professionals]);

  // --- Carga de Datos ---
  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // 1. Invitaciones
      const invRes = await fetch(`${API_URL}/api/assistants/invitations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const invData = await invRes.json();
      if (invData.success) setInvitations(invData.invitations);

      // 2. Profesionales
      const profRes = await fetch(`${API_URL}/api/assistants/my-professionals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const profData = await profRes.json();
      if (profData.success) setProfessionals(profData.professionals);

    } catch (err) {
      setError('Error cargando datos iniciales');
    } finally {
      setLoading(false);
    }
  };

  const loadUnifiedAgenda = async () => {
    try {
      setLoadingAgenda(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/assistants/unified-agenda?date=${selectedDate}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success) {
        setAgenda(data.agenda);
        setSubscriptionWarning(null);
      } else if (data.code === 'ALL_SUBSCRIPTIONS_EXPIRED' || data.code === 'PROFESSIONAL_SUBSCRIPTION_EXPIRED') {
        setSubscriptionWarning(data.message);
        setAgenda([]);
      }
    } catch (err) {
      console.error('Error cargando agenda:', err);
    } finally {
      setLoadingAgenda(false);
    }
  };

  // Actualizar estado del turno
  const updateAppointmentStatus = async (appointmentId: number, status: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/assistants/appointments/${appointmentId}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ status })
        }
      );
      const data = await response.json();
      if (data.success) {
        loadUnifiedAgenda(); // Recargar agenda
      } else if (data.code === 'PROFESSIONAL_SUBSCRIPTION_EXPIRED') {
        setSubscriptionWarning(data.message);
      } else {
        setError(data.error || data.message || 'Error actualizando el turno');
      }
    } catch (err) {
      setError('Error de conexión');
    }
  };

  // "Borrar cancelado": internamente es soft-hide REVERSIBLE (no se borra, queda
  // como registro y se puede recuperar). La confirmación la maneja el confirmModal.
  const hideAppointment = async (appointmentId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/assistants/appointments/${appointmentId}/hide`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        loadUnifiedAgenda(); // Recargar agenda
      } else if (data.code === 'PROFESSIONAL_SUBSCRIPTION_EXPIRED') {
        setSubscriptionWarning(data.message);
      } else {
        setError(data.error || data.message || 'Error quitando el turno');
      }
    } catch (err) {
      setError('Error de conexión');
    }
  };

  // Adjuntar comprobante a un turno YA CREADO (queda auto-verificado)
  const triggerComprobanteUpload = (appointmentId: number) => {
    comprobanteTargetIdRef.current = appointmentId;
    comprobanteInputRef.current?.click();
  };

  const onComprobanteFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const appointmentId = comprobanteTargetIdRef.current;
    e.target.value = ''; // permitir re-subir el mismo archivo después
    if (!file || !appointmentId) return;
    try {
      setUploadingComprobanteId(appointmentId);
      const token = localStorage.getItem('token');
      // 1) Subir el archivo y obtener la URL
      const fd = new FormData();
      fd.append('proof', file);
      const upRes = await fetch(`${API_URL}/api/payments/verification/upload-proof`, { method: 'POST', body: fd });
      const upData = await upRes.json();
      if (!upData.success || !upData.proofUrl) {
        setError('No se pudo subir el comprobante. Probá de nuevo.');
        return;
      }
      // 2) Adjuntarlo al turno existente (auto-verificado)
      const response = await fetch(
        `${API_URL}/api/assistants/appointments/${appointmentId}/comprobante`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ comprobanteUrl: upData.proofUrl })
        }
      );
      const data = await response.json();
      if (data.success) {
        loadUnifiedAgenda();
      } else if (data.code === 'PROFESSIONAL_SUBSCRIPTION_EXPIRED') {
        setSubscriptionWarning(data.message);
      } else {
        setError(data.error || data.message || 'Error adjuntando el comprobante');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setUploadingComprobanteId(null);
      comprobanteTargetIdRef.current = null;
    }
  };

  // Quitar el comprobante de un turno (subido por error) → vuelve a "no pagado"
  const removeComprobante = async (appointmentId: number) => {
    try {
      setUploadingComprobanteId(appointmentId);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/assistants/appointments/${appointmentId}/comprobante`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success) {
        loadUnifiedAgenda();
      } else if (data.code === 'PROFESSIONAL_SUBSCRIPTION_EXPIRED') {
        setSubscriptionWarning(data.message);
      } else {
        setError(data.error || data.message || 'Error quitando el comprobante');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setUploadingComprobanteId(null);
    }
  };

  // --- Manejadores de Invitaciones ---
  const handleAcceptInvitation = async (invitationId: number) => {
    try {
      setProcessingInvitation(invitationId);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/assistants/invitations/${invitationId}/accept`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) loadData();
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleRejectInvitation = async (invitationId: number) => {
    if (!window.confirm('¿Estás seguro de rechazar esta invitación?')) return;
    try {
      setProcessingInvitation(invitationId);
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/assistants/invitations/${invitationId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadData();
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setProcessingInvitation(null);
    }
  };

  // --- Utilidades ---
  const changeDate = (days: number) => {
    // Parsear como fecha LOCAL (no UTC) para evitar corrimientos de día
    const [y, m, d] = selectedDate.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + days);
    setSelectedDate(getLocalDateStr(date));
  };

  const formatDate = (dateString: string) => {
    // Usamos 'es-AR' para formato argentino
    return new Date(dateString + 'T12:00:00').toLocaleDateString('es-AR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  const getStatusColor = (estado: string) => {
    switch (estado) {
      case 'confirmed': return 'bg-green-50 text-green-700 border-green-200';
      case 'pending': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'cancelled': return 'bg-red-50 text-red-700 border-red-200';
      case 'completed': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'no_show': return 'bg-gray-50 text-gray-700 border-gray-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getStatusLabel = (estado: string) => {
    switch (estado) {
      case 'confirmed': return 'Confirmado';
      case 'pending': return 'Pendiente';
      case 'cancelled': return 'Cancelado';
      case 'completed': return 'Completado';
      case 'no_show': return 'No asistió';
      default: return estado;
    }
  };

  // Obtener sedes únicas de la agenda actual (para filtro)
  const uniqueLocations = (() => {
    const locMap = new Map<number, { id: number; name: string; city?: string }>();
    agenda.forEach(item => {
      item.appointments.forEach(apt => {
        if (apt.location_id && apt.location_name && !locMap.has(apt.location_id)) {
          locMap.set(apt.location_id, { id: apt.location_id, name: apt.location_name, city: apt.location_city });
        }
      });
    });
    return Array.from(locMap.values());
  })();

  // --- Exportar Agenda a Excel (via Backend) ---
  const exportToExcel = async (fromDate: string, toDate: string) => {
    const professionalId = selectedProfFilter === 'all'
      ? professionals[0]?.professional_id
      : selectedProfFilter;

    if (!professionalId) {
      alert('No hay profesional seleccionado');
      return;
    }

    const token = localStorage.getItem('token');
    const response = await fetch(
      `${API_URL}/api/appointments/professional/${professionalId}/export/excel?from=${fromDate}&to=${toDate}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Error al exportar agenda');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agenda_${fromDate}_${toDate}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // --- Cálculos de Estadísticas ---
  const calculateStats = () => {
    let totalAppointments = 0;
    let pending = 0;
    let confirmed = 0;

    agenda.forEach(item => {
      // Filtrar si hay un profesional seleccionado
      if (selectedProfFilter !== 'all' && item.professional.professional_id !== selectedProfFilter) return;
      
      item.appointments.forEach(apt => {
        totalAppointments++;
        if (apt.estado === 'pending') pending++;
        if (apt.estado === 'confirmed') confirmed++;
      });
    });

    return { totalAppointments, pending, confirmed };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size={48} label="Cargando..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-12 overflow-x-hidden">
      {/* Top Navigation Bar */}
      <nav className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 sticky top-0 z-20">
        {/* flex-wrap: en pantallas chicas la fila envuelve en vez de desbordar
            el viewport (el desborde corría los elementos fixed en móvil) */}
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-y-2">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 text-white p-2 rounded-lg">
              <Briefcase className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">
                {userName ? `¡Bienvenido/a, ${userName}!` : '¡Bienvenido/a!'} 👋
              </h1>
              <p className="text-xs text-gray-600">
                Gestionando {professionals.length} {professionals.length === 1 ? 'profesional' : 'profesionales'}
              </p>
            </div>

            {/* Widget de clima */}
            <WeatherWidget className="hidden sm:flex ml-2" />

            {/* Ancla del badge de referidos colapsado (ReferralBadge se
                monta acá vía portal cuando está oculto) */}
            <span id="referral-badge-anchor" className="flex items-center" />
          </div>
          
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center bg-gray-100 rounded-lg px-3 py-2">
              <Search className="w-4 h-4 text-gray-400 mr-2" />
              <input 
                type="text" 
                placeholder="Buscar paciente..." 
                className="bg-transparent border-none outline-none text-sm w-48"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {/* Botón Recordatorios manuales (pivot migración 011) */}
            <div className="flex items-center gap-1">
              <button
                onClick={openRemindersPanel}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                title="Ver lista de turnos para enviar recordatorios por WhatsApp"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Recordatorios</span>
              </button>
              <button
                onClick={() => setShowRemindersGuide(true)}
                className="p-2 text-emerald-700 hover:bg-emerald-50 rounded-lg"
                title="¿Cómo funcionan los recordatorios?"
                aria-label="Ver guía de recordatorios"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
            </div>

            <button className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full">
              <Bell className="w-6 h-6" />
              {invitations.length > 0 && (
                <span className="absolute top-1 right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></span>
              )}
            </button>
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold cursor-pointer">
              A
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* Programa Embajadoras (referidos) */}
        <ReferralBadge className="mb-6" />

        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 flex items-center justify-between rounded shadow-sm">
            <span className="flex items-center gap-2"><AlertCircle className="w-5 h-5"/> {error}</span>
            <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Subscription Warning Banner */}
        {subscriptionWarning && (
          <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-500 text-amber-800 flex items-center justify-between rounded shadow-sm">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0" />
              <div>
                <p className="font-semibold">Suscripción vencida</p>
                <p className="text-sm text-amber-700">{subscriptionWarning}</p>
              </div>
            </div>
            <button onClick={() => setSubscriptionWarning(null)}><X className="w-4 h-4 text-amber-500" /></button>
          </div>
        )}

        {/* Profesionales con suscripción vencida */}
        {professionals.some(p => p.subscription_active === false) && (
          <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              <h3 className="font-semibold text-orange-800">Profesionales con suscripción inactiva</h3>
            </div>
            <div className="space-y-1">
              {professionals.filter(p => p.subscription_active === false).map(prof => (
                <p key={prof.professional_id} className="text-sm text-orange-700 flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                  <span className="font-medium">{prof.nombre} {prof.apellido}</span>
                  <span className="text-orange-500">— {prof.subscription_label || 'Suscripción vencida'}</span>
                </p>
              ))}
            </div>
            <p className="text-xs text-orange-600 mt-2">
              No podrás gestionar turnos de estos profesionales hasta que renueven su plan.
            </p>
          </div>
        )}

        {/* Invitaciones Pendientes */}
        {invitations.length > 0 && (
          <div className="mb-8 bg-yellow-50 border border-yellow-200 rounded-xl p-4 shadow-sm">
            <h2 className="font-semibold text-yellow-800 flex items-center gap-2 mb-3">
              <Bell className="w-5 h-5 animate-bounce" />
              Tienes {invitations.length} invitaciones pendientes
            </h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {invitations.map((inv) => (
                <div key={inv.invitation_id} className="bg-white p-4 rounded-lg border border-yellow-100 shadow-sm flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden">
                      {inv.picture ? <img src={inv.picture} className="w-full h-full object-cover" /> : <User className="w-6 h-6 m-2 text-gray-500" />}
                    </div>
                    <div>
                      <p className="font-bold text-sm">{inv.nombre} {inv.apellido}</p>
                      <p className="text-xs text-gray-600">{inv.especialidad}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button title="Aceptar" onClick={() => handleAcceptInvitation(inv.invitation_id)} className="p-2 bg-green-100 text-green-700 rounded hover:bg-green-200"><Check className="w-4 h-4" /></button>
                    <button title="Rechazar" onClick={() => handleRejectInvitation(inv.invitation_id)} className="p-2 bg-red-100 text-red-700 rounded hover:bg-red-200"><X className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* --- CONTENIDO PRINCIPAL --- */}
        {professionals.length === 0 && invitations.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl shadow-sm border border-gray-200">
            <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-12 h-12 text-blue-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Tu panel está vacío</h2>
            <p className="text-gray-500 mt-2 max-w-md mx-auto">
              Aún no gestionas la agenda de ningún profesional. Pídeles que te envíen una invitación a tu correo.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            
            {/* 1. KPIs Principales */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="text-gray-500 text-xs font-medium uppercase tracking-wider mb-1">Total Turnos Hoy</div>
                <div className="flex items-end justify-between">
                  <span className="text-3xl font-bold text-gray-900">{stats.totalAppointments}</span>
                  <CalendarCheck className="w-6 h-6 text-blue-500 opacity-50" />
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="text-gray-500 text-xs font-medium uppercase tracking-wider mb-1">Confirmados</div>
                <div className="flex items-end justify-between">
                  <span className="text-3xl font-bold text-green-600">{stats.confirmed}</span>
                  <Check className="w-6 h-6 text-green-500 opacity-50" />
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="text-gray-500 text-xs font-medium uppercase tracking-wider mb-1">Pendientes</div>
                <div className="flex items-end justify-between">
                  <span className="text-3xl font-bold text-yellow-600">{stats.pending}</span>
                  <Clock className="w-6 h-6 text-yellow-500 opacity-50" />
                </div>
              </div>
              <button 
                onClick={() => setShowNewAppointmentModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-xl shadow-md transition-all flex flex-col items-center justify-center gap-1"
              >
                <Plus className="w-6 h-6" />
                <span className="font-semibold">Nuevo Turno</span>
              </button>
            </div>

            {/* 2. Filtros y Fecha */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white p-3 rounded-xl shadow-sm border border-gray-200">
              
              {/* Selector de Profesional */}
              <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-hide">
                <button
                  onClick={() => setSelectedProfFilter('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedProfFilter === 'all' 
                      ? 'bg-gray-900 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Todos
                </button>
                {professionals.map(prof => (
                  <button
                    key={prof.professional_id}
                    onClick={() => setSelectedProfFilter(prof.professional_id)}
                    className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors ${
                      prof.subscription_active === false
                        ? 'bg-orange-50 border border-orange-300 text-orange-600 opacity-70 cursor-not-allowed'
                        : selectedProfFilter === prof.professional_id
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-white border border-gray-200 text-gray-700 hover:border-blue-300'
                    }`}
                    title={prof.subscription_active === false ? `Suscripción: ${prof.subscription_label}` : ''}
                  >
                    {prof.picture ? (
                      <img src={prof.picture} className="w-5 h-5 rounded-full object-cover" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold">
                        {prof.nombre[0]}
                      </div>
                    )}
                    {prof.nombre}
                    {prof.subscription_active === false && (
                      <AlertCircle className="w-3.5 h-3.5 text-orange-500" />
                    )}
                  </button>
                ))}
              </div>

              {/* Filtro de Sede (solo si hay sedes) */}
              {uniqueLocations.length > 1 && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-purple-500 flex-shrink-0" />
                  <select
                    value={selectedLocationFilter === 'all' ? 'all' : selectedLocationFilter}
                    onChange={(e) => setSelectedLocationFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                    className="text-sm border border-purple-200 bg-purple-50 text-purple-700 rounded-full px-3 py-1.5 font-medium focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                  >
                    <option value="all">Todas las sedes</option>
                    {uniqueLocations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}{loc.city ? ` - ${loc.city}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Selector de Fecha + Acciones */}
              <div className="flex flex-wrap items-center gap-2 w-full md:w-auto md:justify-end">
                <div className="flex items-center bg-gray-50 rounded-lg p-1 border border-gray-200 w-full md:w-auto justify-between">
                  <button onClick={() => changeDate(-1)} className="p-2 hover:bg-white hover:shadow-sm rounded-md transition-all text-gray-600">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="px-2 md:px-4 text-center flex-1 md:flex-none md:min-w-[200px]">
                    <span className="block text-sm font-bold text-gray-900 capitalize">{formatDate(selectedDate)}</span>
                  </div>
                  <button onClick={() => changeDate(1)} className="p-2 hover:bg-white hover:shadow-sm rounded-md transition-all text-gray-600">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="relative">
                    <input 
                      type="date" 
                      value={selectedDate} 
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-8 h-8 opacity-0 absolute inset-0 cursor-pointer z-10" 
                    />
                    <Calendar className="w-5 h-5 text-blue-600 ml-2" />
                  </div>
                </div>
                
                {/* Compactar / desplegar todos los turnos del día */}
                <button
                  onClick={toggleCollapseAll}
                  className="flex-1 md:flex-none justify-center flex items-center gap-1.5 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-lg transition-colors text-sm font-medium"
                  title={allCollapsed ? 'Mostrar el detalle de todos los turnos' : 'Compactar todos los turnos'}
                >
                  {allCollapsed ? <ChevronsUpDown className="w-4 h-4" /> : <ChevronsDownUp className="w-4 h-4" />}
                  <span className="hidden sm:inline">{allCollapsed ? 'Desplegar' : 'Compactar'}</span>
                </button>

                {/* Botón Exportar Excel */}
                <button
                  onClick={() => setShowExportModal(true)}
                  className="flex-1 md:flex-none justify-center flex items-center gap-1.5 px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 rounded-lg transition-colors text-sm font-medium"
                  title="Exportar agenda a Excel"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  <span className="hidden sm:inline">Excel</span>
                </button>

                {/* Botón Calendario */}
                <button
                  onClick={() => navigate('/assistant/calendar')}
                  className="flex-1 md:flex-none justify-center flex items-center gap-1.5 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg transition-colors text-sm font-medium"
                  title="Ver calendario completo"
                >
                  <LayoutGrid className="w-4 h-4" />
                  <span className="hidden sm:inline">Calendario</span>
                </button>

                {/* Botón Horarios: los horarios de la profesional (ver y cambiar) */}
                <button
                  onClick={() => {
                    if (selectedProfFilter !== 'all') irAHorarios(selectedProfFilter);
                    else if (professionals.length === 1) irAHorarios(professionals[0].professional_id);
                    else setElegirHorarios(true);
                  }}
                  disabled={professionals.length === 0}
                  className="flex-1 md:flex-none justify-center flex items-center gap-1.5 px-3 py-2 bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
                  title="Ver y cambiar los horarios, días especiales y bloqueos"
                >
                  <ListChecks className="w-4 h-4" />
                  <span className="hidden sm:inline">Horarios</span>
                </button>

                {/* Boton Verificar Transferencias - habilitado solo si hay un profesional especifico seleccionado */}
                <button
                  onClick={() => {
                    if (selectedProfFilter === 'all') {
                      if (professionals.length === 1) {
                        navigate(`/assistant/payment-verification?professionalId=${professionals[0].professional_id}`);
                      } else {
                        alert('Primero seleccioná un profesional en el filtro de arriba para ver sus transferencias.');
                      }
                      return;
                    }
                    navigate(`/assistant/payment-verification?professionalId=${selectedProfFilter}`);
                  }}
                  className="flex-1 md:flex-none justify-center flex items-center gap-1.5 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg transition-colors text-sm font-medium"
                  title="Ver comprobantes de transferencia del profesional seleccionado"
                >
                  <Banknote className="w-4 h-4" />
                  <span className="hidden sm:inline">Transferencias</span>
                </button>

                {/* Botón Precios de servicios */}
                <button
                  onClick={() => setShowServicesModal(true)}
                  className="flex-1 md:flex-none justify-center flex items-center gap-1.5 px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 rounded-lg transition-colors text-sm font-medium"
                  title="Editar precios de los servicios"
                >
                  <DollarSign className="w-4 h-4" />
                  <span className="hidden sm:inline">Precios</span>
                </button>
              </div>
            </div>

            {/* 3. Agenda Detallada */}
            <div className="relative">
              {loadingAgenda && (
                <div className="absolute inset-0 z-20 bg-white/70 backdrop-blur-sm flex flex-col items-center justify-center rounded-xl min-h-[200px]">
                  <LoadingSpinner size={40} label="Cargando turnos..." />
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {agenda
                .filter(item => selectedProfFilter === 'all' || item.professional.professional_id === selectedProfFilter)
                .map((item, idx) => {
                  // Filtrar turnos por sede si hay filtro activo
                  const filteredAppointments = selectedLocationFilter === 'all'
                    ? item.appointments
                    : item.appointments.filter(apt => apt.location_id === selectedLocationFilter);

                  // Animación de entrada: alterna lados y escalona por posición
                  const animClass = idx % 2 === 0 ? 'animate-slide-in-left' : 'animate-slide-in-right';

                  return (
                <div
                  key={item.professional.professional_id}
                  className={`bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-full ${animClass}`}
                  style={{ animationDelay: `${idx * 80}ms` }}
                >

                  {/* Cabecera del Profesional */}
                  <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white shadow-sm">
                        {item.professional.picture ? (
                          <img src={item.professional.picture} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                            {item.professional.nombre[0]}
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">{item.professional.nombre} {item.professional.apellido}</h3>
                        <p className="text-xs text-gray-600">{item.professional.especialidad}</p>
                      </div>
                    </div>
                    <div className="text-xs font-semibold bg-white px-3 py-1 rounded-full border border-gray-200 text-gray-600">
                      {filteredAppointments.length} Turnos
                    </div>
                  </div>

                  {/* Lista de Turnos */}
                  <div className="p-4 flex-1">
                    {filteredAppointments.length === 0 ? (
                      <div className="h-40 flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-100 rounded-xl">
                        <Clock className="w-8 h-8 mb-2 opacity-50" />
                        <p className="text-sm">{selectedLocationFilter !== 'all' ? 'Sin turnos en esta sede' : 'Agenda libre'}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {filteredAppointments
                          .filter(apt => apt.client_nombre.toLowerCase().includes(searchTerm.toLowerCase()))
                          .map((apt) => (
                          <div
                            key={apt.id}
                            className={`relative group p-4 rounded-xl border-l-4 shadow-sm hover:shadow-md transition-all bg-white border border-gray-100 text-left ${
                              apt.estado === 'confirmed' ? 'border-l-green-500' :
                              apt.estado === 'pending' ? 'border-l-yellow-500' :
                              apt.estado === 'cancelled' ? 'border-l-red-500' : 'border-l-blue-500'
                            }`}
                          >
                            <div className="flex justify-between items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className="text-lg font-bold text-gray-900 tabular-nums leading-none">{apt.hora.slice(0, 5)}</span>
                                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${getStatusColor(apt.estado)}`}>
                                    {getStatusLabel(apt.estado)}
                                  </span>
                                  {/* Compactado con nota: avisar que la tiene */}
                                  {collapsedIds.has(apt.id) && apt.notas && (
                                    <span
                                      title={apt.notas}
                                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300"
                                    >
                                      <FileText className="w-3 h-3" /> Nota
                                    </span>
                                  )}
                                </div>
                                <h4 className="font-bold text-gray-900 text-[15px] leading-tight truncate">
                                  {apt.client_nombre} {apt.client_apellido}
                                </h4>
                                <p className="text-sm text-gray-500 mt-0.5">
                                  {apt.service_name} · {apt.service_duration} min
                                </p>
                                {apt.location_name && !collapsedIds.has(apt.id) && (
                                  <span className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-100 text-purple-800 text-[11px] font-semibold border border-purple-300">
                                    <MapPin className="w-3 h-3" /> {apt.location_name}
                                  </span>
                                )}

                                {!collapsedIds.has(apt.id) && (
                                <>

                                {/* Tracking: quién confirmó/canceló */}
                                {apt.confirmed_by && apt.estado === 'confirmed' && (
                                  <div className="mt-1.5 flex items-center gap-1.5">
                                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                                      <CheckCircle className="w-3 h-3" />
                                      Confirmado por {apt.confirmed_by === 'patient' ? 'paciente' : apt.confirmed_by === 'professional' ? 'profesional' : apt.confirmed_by === 'assistant' ? 'secretaria' : apt.confirmed_by}
                                      {apt.confirmed_at && (
                                        <span className="text-green-500 ml-1">
                                          {new Date(apt.confirmed_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                )}
                                {apt.estado === 'pending' && !apt.confirmed_by && (
                                  <div className="mt-1.5">
                                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                      <Clock className="w-3 h-3" />
                                      Sin confirmar por paciente
                                    </span>
                                  </div>
                                )}
                                {apt.cancelled_by && apt.estado === 'cancelled' && (
                                  <div className="mt-1.5 flex flex-col gap-1">
                                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                                      <X className="w-3 h-3" />
                                      Cancelado por {apt.cancelled_by === 'patient' ? 'paciente' : apt.cancelled_by === 'professional' ? 'profesional' : apt.cancelled_by === 'assistant' ? 'secretaria' : apt.cancelled_by}
                                    </span>
                                    {apt.cancellation_reason && (
                                      <span className="text-xs text-red-500 ml-1">Motivo: {apt.cancellation_reason}</span>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => setConfirmModal({
                                        title: 'Borrar cancelado',
                                        message: `Se quitará de la agenda el turno cancelado de ${apt.client_nombre}. Queda guardado por si necesitás recuperarlo.`,
                                        confirmLabel: 'Borrar',
                                        onConfirm: () => hideAppointment(apt.id)
                                      })}
                                      className="self-start mt-1 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                                      title="Quita el turno cancelado de la agenda (no se borra, queda como registro)"
                                    >
                                      <X className="w-3 h-3" />
                                      Borrar
                                    </button>
                                  </div>
                                )}

                                <div className="flex items-center gap-3 mt-2 flex-wrap font-semibold">
                                  {apt.client_telefono && (
                                    <a href={`https://wa.me/${apt.client_telefono.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-green-600 hover:underline">
                                      <Phone className="w-3 h-3" /> WhatsApp
                                    </a>
                                  )}
                                  
                                  {/* Estado de confirmación WhatsApp */}
                                  {apt.whatsapp_confirmation ? (
                                    apt.whatsapp_confirmation.status === 'sent' ? (
                                      <span className="flex items-center gap-1 text-xs text-green-600" title={`Enviado: ${new Date(apt.whatsapp_confirmation.created_at).toLocaleString('es-AR')}`}>
                                        <MessageCircle className="w-3 h-3" />
                                        ✓ Confirmación enviada
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-xs text-red-500" title={apt.whatsapp_confirmation.error_message || 'Error al enviar'}>
                                        <MessageCircle className="w-3 h-3" />
                                        ✗ Error confirmación
                                      </span>
                                    )
                                  ) : apt.client_telefono ? (
                                    <span className="flex items-center gap-1 text-xs text-gray-700">
                                      <MessageCircle className="w-3 h-3" />
                                      Sin confirmación WA
                                    </span>
                                  ) : null}

                                  {/* Estado de recordatorio WA */}
                                  {apt.whatsapp_reminder ? (
                                    apt.whatsapp_reminder.status === 'sent' ? (
                                      <span className="flex items-center gap-1 text-xs text-blue-600" title={`Enviado: ${new Date(apt.whatsapp_reminder.created_at).toLocaleString('es-AR')}`}>
                                        <Bell className="w-3 h-3" />
                                        ✓ Recordatorio enviado
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-xs text-red-500" title={apt.whatsapp_reminder.error_message || 'Error'}>
                                        <Bell className="w-3 h-3" />
                                        ✗ Error recordatorio
                                      </span>
                                    )
                                  ) : apt.reminder_sent ? (
                                    <span className="flex items-center gap-1 text-xs text-blue-600" title={`Enviado: ${apt.reminder_sent_at ? new Date(apt.reminder_sent_at).toLocaleString('es-AR') : ''}`}>
                                      <Bell className="w-3 h-3" />
                                      ✓ Recordatorio enviado
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-xs text-gray-700">
                                      <Bell className="w-3 h-3" />
                                      Recordatorio pendiente
                                    </span>
                                  )}
                                </div>

                                {/* Indicadores de Email */}
                                <div className="flex items-center gap-3 mt-1.5 flex-wrap font-semibold">
                                  {/* Email de confirmación */}
                                  {apt.email_confirmation ? (
                                    apt.email_confirmation.status === 'sent' ? (
                                      <span className="flex items-center gap-1 text-xs text-emerald-600" title={`Enviado: ${new Date(apt.email_confirmation.created_at).toLocaleString('es-AR')}`}>
                                        <Mail className="w-3 h-3" />
                                        ✓ Email confirmación
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-xs text-red-500" title={apt.email_confirmation.error_message || 'Error al enviar'}>
                                        <Mail className="w-3 h-3" />
                                        ✗ Error email
                                      </span>
                                    )
                                  ) : apt.client_email ? (
                                    <span className="flex items-center gap-1 text-xs text-gray-700">
                                      <Mail className="w-3 h-3" />
                                      Sin email confirm.
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-xs text-gray-600">
                                      <Mail className="w-3 h-3" />
                                      Sin email
                                    </span>
                                  )}

                                  {/* Email de recordatorio */}
                                  {apt.email_reminder ? (
                                    apt.email_reminder.status === 'sent' ? (
                                      <span className="flex items-center gap-1 text-xs text-teal-600" title={`Enviado: ${new Date(apt.email_reminder.created_at).toLocaleString('es-AR')}`}>
                                        <Mail className="w-3 h-3" />
                                        ✓ Email recordatorio
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-xs text-red-500" title={apt.email_reminder.error_message || 'Error'}>
                                        <Mail className="w-3 h-3" />
                                        ✗ Error email rec.
                                      </span>
                                    )
                                  ) : (
                                    <span className="flex items-center gap-1 text-xs text-gray-700">
                                      <Mail className="w-3 h-3" />
                                      Email rec. pendiente
                                    </span>
                                  )}
                                </div>

                                {/* Comprobante de pago: subir a un turno ya creado */}
                                {apt.estado !== 'cancelled' && (
                                  <div className="flex items-center gap-3 mt-1.5 flex-wrap font-semibold">
                                    {apt.comprobante_pago_url ? (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => setComprobanteViewerUrl(apt.comprobante_pago_url || null)}
                                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-200 transition-colors"
                                          title="Ver comprobante adjuntado"
                                        >
                                          <FileText className="w-3 h-3" /> ✓ Comprobante {apt.pago_verificado ? 'verificado' : 'subido'}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => triggerComprobanteUpload(apt.id)}
                                          disabled={uploadingComprobanteId === apt.id}
                                          className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 hover:text-gray-900 transition-colors disabled:opacity-50"
                                        >
                                          {uploadingComprobanteId === apt.id ? 'Subiendo…' : 'Reemplazar'}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setConfirmModal({
                                            title: 'Quitar comprobante',
                                            message: `Se va a quitar el comprobante del turno de ${apt.client_nombre} y el turno volverá a figurar como NO pagado. ¿Continuar?`,
                                            confirmLabel: 'Quitar',
                                            onConfirm: () => removeComprobante(apt.id)
                                          })}
                                          disabled={uploadingComprobanteId === apt.id}
                                          className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-700 border border-red-300 hover:bg-red-200 hover:text-red-800 transition-colors disabled:opacity-50"
                                        >
                                          Quitar
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => triggerComprobanteUpload(apt.id)}
                                        disabled={uploadingComprobanteId === apt.id}
                                        className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 hover:text-gray-900 transition-colors disabled:opacity-50"
                                      >
                                        <FileText className="w-3 h-3" /> {uploadingComprobanteId === apt.id ? 'Subiendo…' : 'Subir comprobante'}
                                      </button>
                                    )}
                                  </div>
                                )}
                                </>
                                )}
                              </div>

                              {/* Botones de acción rápida */}
                              <div className="flex flex-col gap-1">
                                <button
                                  type="button"
                                  onClick={() => toggleCollapsed(apt.id)}
                                  title={collapsedIds.has(apt.id) ? 'Ver detalle del turno' : 'Compactar turno'}
                                  className="p-2 text-gray-600 bg-gray-100 border border-gray-300 hover:bg-gray-200 hover:text-gray-900 rounded-full transition-colors"
                                >
                                  {collapsedIds.has(apt.id)
                                    ? <ChevronDown className="w-4 h-4" />
                                    : <ChevronUp className="w-4 h-4" />}
                                </button>
                                {apt.estado === 'pending' && (
                                  <button 
                                    title="Confirmar turno" 
                                    onClick={() => updateAppointmentStatus(apt.id, 'confirmed')}
                                    className="p-2 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                )}
                                {(apt.estado === 'pending' || apt.estado === 'confirmed') && (
                                  <button
                                    title="Cancelar turno"
                                    onClick={() => setConfirmModal({
                                      title: 'Cancelar turno',
                                      message: `¿Seguro que querés cancelar el turno de ${apt.client_nombre} a las ${apt.hora.slice(0,5)}? El paciente quedará marcado como cancelado.`,
                                      confirmLabel: 'Sí, cancelar',
                                      onConfirm: () => updateAppointmentStatus(apt.id, 'cancelled')
                                    })}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                                {apt.estado === 'confirmed' && (
                                  <button 
                                    title="Marcar como completado" 
                                    onClick={() => updateAppointmentStatus(apt.id, 'completed')}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                            
                            {/* Nota del turno: editable en la misma tarjeta.
                                Al compactar se oculta (queda el chip "Nota" arriba). */}
                            {!collapsedIds.has(apt.id) && (
                            <AppointmentNoteEditor
                              appointmentId={apt.id}
                              value={apt.notas}
                              asAssistant
                              onSaved={(newNotes) => {
                                setAgenda(prev => prev.map(item => ({
                                  ...item,
                                  appointments: item.appointments.map(a =>
                                    a.id === apt.id ? { ...a, notas: newNotes } : a
                                  )
                                })));
                              }}
                            />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {/* ¿De quién son los horarios? (en el body, por encima del encabezado) */}
        {elegirHorarios && createPortal(
          <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setElegirHorarios(false)}>
            <div
              role="dialog"
              aria-label="Elegir profesional"
              className="bg-white w-full sm:max-w-sm max-h-[85dvh] overflow-y-auto rounded-t-3xl sm:rounded-3xl shadow-2xl p-4 sm:p-5 text-left"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-3">
                <h2 className="flex-1 text-lg font-bold text-gray-900">¿De quién son los horarios?</h2>
                <button type="button" onClick={() => setElegirHorarios(false)} aria-label="Cerrar" className="p-2 rounded-xl text-gray-500 hover:bg-gray-100">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-2">
                {professionals.map((p) => (
                  <button
                    key={p.professional_id}
                    type="button"
                    onClick={() => irAHorarios(p.professional_id)}
                    className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-left hover:border-violet-300 hover:bg-violet-50 transition-colors"
                  >
                    <span className="block text-sm font-semibold text-gray-900">{p.nombre} {p.apellido}</span>
                    {p.especialidad && <span className="block text-xs text-gray-500">{p.especialidad}</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Modal de Nuevo Turno */}
        <NewAppointmentModal
          isOpen={showNewAppointmentModal}
          onClose={() => setShowNewAppointmentModal(false)}
          onSuccess={() => loadUnifiedAgenda()}
          professionals={professionals}
          preselectedProfessional={selectedProfFilter !== 'all' ? selectedProfFilter : undefined}
          preselectedDate={selectedDate}
        />

        {/* Panel de Recordatorios manuales */}
        {remindersPanelProfId !== null && (
          <RemindersPanel
            professionalId={remindersPanelProfId}
            open={remindersPanelOpen}
            onClose={() => setRemindersPanelOpen(false)}
          />
        )}

        {/* Guía wizard de recordatorios manuales (primer uso) */}
        <NewRemindersGuideModal
          open={showRemindersGuide}
          onClose={() => setShowRemindersGuide(false)}
        />

        {/* Modal de Exportar Agenda */}
        <ExportDateRangeModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={exportToExcel}
          title="Exportar Agenda"
          description="Selecciona el rango de fechas para exportar"
        />

        {/* Input oculto para adjuntar comprobante a un turno existente */}
        <input
          ref={comprobanteInputRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={onComprobanteFileChange}
        />

        {/* Visor de comprobante (imagen o PDF) */}
        <ComprobanteViewerModal
          url={comprobanteViewerUrl}
          onClose={() => setComprobanteViewerUrl(null)}
        />

        {/* Editar precios de servicios */}
        <AssistantServicesModal
          isOpen={showServicesModal}
          onClose={() => setShowServicesModal(false)}
          professionals={professionals}
          preselectedProfId={selectedProfFilter !== 'all' ? selectedProfFilter : undefined}
        />

        {/* Modal de confirmación estético (cancelar turno / borrar cancelado) */}
        {confirmModal && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4"
            onClick={() => setConfirmModal(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-modal-in"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1.5">{confirmModal.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{confirmModal.message}</p>
              </div>
              <div className="flex gap-2 p-4 pt-0">
                <button
                  type="button"
                  onClick={() => setConfirmModal(null)}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors"
                >
                  Volver
                </button>
                <button
                  type="button"
                  onClick={() => { const cb = confirmModal.onConfirm; setConfirmModal(null); cb(); }}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white font-medium text-sm hover:bg-red-700 transition-colors shadow-sm"
                >
                  {confirmModal.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantDashboard;